import { riskApi } from './riskApi';

/**
 * The one place that talks to the DHF API (`/api/dhf/*`, v4.34.0). Same `call` contract as
 * Risk: CSRF on every write, FastAPI's `detail` surfaced as the error message.
 *
 * Build / Refresh are the trace panel's own funnel routes — one build serves Funnel, Dashboard
 * and DHF — polled on the shared export-job route.
 */
const { call, q } = riskApi;

export const dhfApi = {
  panels: () => call('GET', '/api/dhf/panels'),
  link: (setupId) => call('POST', '/api/dhf/panels', { setup_id: setupId }),
  unlink: (setupId) => call('DELETE', `/api/dhf/panels/${setupId}`),
  saveSettings: (setupId, body) => call('PUT', `/api/dhf/panels/${setupId}/settings`, body),
  get: (setupId) => call('GET', `/api/dhf/${setupId}`),
  items: (setupId, params) => call('GET', `/api/dhf/${setupId}/items${q(params)}`),
  document: (setupId, docId) => call('GET', `/api/dhf/${setupId}/documents/${docId}`),
  reloadDocument: (setupId, docId) => call('POST', `/api/dhf/${setupId}/documents/${docId}/reload`),
  place: (setupId, body) => call('POST', `/api/dhf/${setupId}/place`, body),
  placement: (setupId, levelNum, blockId) =>
    call('GET', `/api/dhf/placement${q({ setup_id: setupId, level_num: levelNum, block_id: blockId })}`),
  outline: (setupId, docId) => call('GET', `/api/dhf/outline/${docId}${q({ setup_id: setupId })}`),
  // Baselines + compare (2026-10-27) — `b` 'now' = the latest build.
  baselines: (setupId) => call('GET', `/api/dhf/${setupId}/baselines`),
  takeBaseline: (setupId, body) => call('POST', `/api/dhf/${setupId}/baselines`, body),
  archiveBaseline: (setupId, id, archived = true) =>
    call('POST', `/api/dhf/${setupId}/baselines/${id}/archive${q({ archived })}`),
  compare: (setupId, a, b = 'now') => call('GET', `/api/dhf/${setupId}/compare${q({ a, b })}`),
  analyse: (setupId, body) => call('POST', `/api/dhf/${setupId}/compare/analysis`, body),
  // Document Variants (2026-10-27) — forks of one document, kept here; Orcanos only on merge.
  variants: (setupId, docId) => call('GET', `/api/dhf/${setupId}/documents/${docId}/variants`),
  fork: (setupId, docId, body) => call('POST', `/api/dhf/${setupId}/documents/${docId}/variants`, body),
  compareVariants: (setupId, docId, a, b) =>
    call('GET', `/api/dhf/${setupId}/documents/${docId}/variants/compare${q({ a, b })}`),
  variant: (setupId, vid) => call('GET', `/api/dhf/${setupId}/variants/${vid}`),
  updateVariant: (setupId, vid, body) => call('PUT', `/api/dhf/${setupId}/variants/${vid}`, body),
  archiveVariant: (setupId, vid) => call('DELETE', `/api/dhf/${setupId}/variants/${vid}`),
  addNode: (setupId, vid, body) => call('POST', `/api/dhf/${setupId}/variants/${vid}/nodes`, body),
  editNode: (setupId, vid, nid, body) => call('PATCH', `/api/dhf/${setupId}/variants/${vid}/nodes/${nid}`, body),
  deleteNode: (setupId, vid, nid) => call('DELETE', `/api/dhf/${setupId}/variants/${vid}/nodes/${nid}`),
  moveNode: (setupId, vid, nid, direction) =>
    call('POST', `/api/dhf/${setupId}/variants/${vid}/nodes/${nid}/move`, { direction }),
  copyNode: (setupId, vid, body) => call('POST', `/api/dhf/${setupId}/variants/${vid}/copy`, body),
  pullPlan: (setupId, vid) => call('GET', `/api/dhf/${setupId}/variants/${vid}/pull`),
  pull: (setupId, vid, decisions) => call('POST', `/api/dhf/${setupId}/variants/${vid}/pull`, { decisions }),
  mergePlan: (setupId, vid) => call('GET', `/api/dhf/${setupId}/variants/${vid}/merge`),
  merge: (setupId, vid, nids) => call('POST', `/api/dhf/${setupId}/variants/${vid}/merge`, { nids }),
  // A file is FETCHED, never linked (Critical Note #85).
  exportVariant: async (setupId, vid, format) => {
    const res = await fetch(`/api/dhf/${setupId}/variants/${vid}/export${q({ format })}`, { credentials: 'include' });
    if (!res.ok) throw new Error(`Download failed (${res.status})`);
    const cd = res.headers.get('Content-Disposition') || '';
    const m = cd.match(/filename\*=UTF-8''([^;]+)/);
    return { blob: await res.blob(), name: m ? decodeURIComponent(m[1]) : `variant.${format}` };
  },
  // The trace panel's build. `smart` re-reads only what changed (falls back to a full read).
  build: (setupId, smart = true) =>
    call('POST', `/api/matrix/${setupId}/funnel/${smart ? 'smart-refresh' : 'refresh'}`),
  job: (jobId) => call('GET', `/api/matrix/export-jobs/${jobId}`),
};
